import { supabase } from '@/lib/supabase';
import type {
  TutorConcept,
  TutorCurriculumItem,
  TutorExercise,
  TutorSession,
  TutorMessage,
  TutorRequest,
  TutorResponse,
  TutorMode,
} from '@/types/tutor';

async function getUserId(): Promise<string> {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user?.id) throw new Error('Not authenticated');
  return user.id;
}

function normalizeRow<T>(row: Record<string, unknown>): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    out[k.replace(/_([a-z])/g, (_, l: string) => l.toUpperCase())] = v;
  }
  return out as unknown as T;
}
function normalizeRows<T>(rows: Record<string, unknown>[]): T[] {
  return rows.map((r) => normalizeRow<T>(r));
}

export async function fetchTutorConcepts(): Promise<TutorConcept[]> {
  const { data, error } = await supabase
    .from('tutor_concepts')
    .select('*')
    .order('due_date', { ascending: true });
  if (error) throw new Error(error.message);
  return normalizeRows<TutorConcept>(data ?? []);
}

export async function upsertTutorConcept(
  conceptKey: string,
  updates: Partial<Omit<TutorConcept, 'id' | 'userId' | 'conceptKey' | 'createdAt'>>,
): Promise<void> {
  const userId = await getUserId();
  const { error } = await supabase.rpc('upsert_tutor_concept', {
    p_user_id: userId,
    p_concept_key: conceptKey,
    p_language_pair: updates.languagePair ?? 'de-en',
    p_cefr_level: updates.cefrLevel ?? null,
    p_title: updates.title ?? conceptKey,
    p_description: updates.description ?? null,
    p_srs_state: updates.srsState ?? null,
    p_due_date: updates.dueDate ?? null,
    p_last_reviewed: updates.lastReviewed ?? null,
    p_mastery_level: updates.masteryLevel ?? 0,
    p_strength: updates.strength ?? 0,
  });
  if (error) throw new Error(error.message);
}

export async function fetchTutorSessions(limit = 20): Promise<TutorSession[]> {
  const { data, error } = await supabase
    .from('tutor_sessions')
    .select('*')
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return normalizeRows<TutorSession>(data ?? []);
}

export async function createTutorSession(
  mode: TutorMode,
  conceptKey?: string,
  parentSessionId?: string,
): Promise<TutorSession> {
  const userId = await getUserId();
  const { data, error } = await supabase
    .from('tutor_sessions')
    .insert({
      user_id: userId,
      mode,
      messages: [],
      concept_key: conceptKey ?? null,
      parent_session_id: parentSessionId ?? null,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return normalizeRow<TutorSession>(data);
}

export async function appendTutorMessage(sessionId: string, message: TutorMessage): Promise<void> {
  const { data: existing, error: fetchErr } = await supabase
    .from('tutor_sessions')
    .select('messages')
    .eq('id', sessionId)
    .single();
  if (fetchErr) throw new Error(fetchErr.message);
  const messages = (existing?.messages as TutorMessage[]) ?? [];
  messages.push(message);
  const { error } = await supabase.from('tutor_sessions').update({ messages }).eq('id', sessionId);
  if (error) throw new Error(error.message);
}

export async function updateTutorSession(
  sessionId: string,
  updates: Partial<Omit<TutorSession, 'id' | 'userId' | 'createdAt'>>,
): Promise<void> {
  const map: Record<string, string> = {
    endedAt: 'ended_at',
    mode: 'mode',
    conceptKey: 'concept_key',
    messages: 'messages',
    aiModel: 'ai_model',
    inputTokens: 'input_tokens',
    outputTokens: 'output_tokens',
    sessionSummary: 'session_summary',
    parentSessionId: 'parent_session_id',
  };
  const snake: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(updates)) if (v !== undefined) snake[map[k] ?? k] = v;
  const { error } = await supabase.from('tutor_sessions').update(snake).eq('id', sessionId);
  if (error) throw new Error(error.message);
}

export async function fetchTutorCurriculum(): Promise<TutorCurriculumItem[]> {
  const { data, error } = await supabase
    .from('tutor_curriculum')
    .select('*')
    .order('sequence_order', { ascending: true });
  if (error) throw new Error(error.message);
  return normalizeRows<TutorCurriculumItem>(data ?? []);
}

export async function initializeTutorCurriculum(
  languagePair: string,
  concepts: {
    key: string;
    cefrLevel: string;
    title: string;
    prerequisites: string[];
    assignedGuideId?: string;
  }[],
): Promise<void> {
  const userId = await getUserId();
  const { error } = await supabase.rpc('initialize_tutor_curriculum', {
    p_user_id: userId,
    p_language_pair: languagePair,
    p_concepts: concepts,
  });
  if (error) throw new Error(error.message);
}

export async function updateCurriculumItem(
  id: string,
  updates: Partial<Omit<TutorCurriculumItem, 'id' | 'userId' | 'conceptKey' | 'createdAt'>>,
): Promise<void> {
  const map: Record<string, string> = {
    sequenceOrder: 'sequence_order',
    status: 'status',
    prerequisites: 'prerequisites',
    assignedGuideId: 'assigned_guide_id',
    aiRationale: 'ai_rationale',
    updatedAt: 'updated_at',
  };
  const snake: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(updates)) if (v !== undefined) snake[map[k] ?? k] = v;
  snake.updated_at = new Date().toISOString();
  const { error } = await supabase.from('tutor_curriculum').update(snake).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function fetchTutorExercises(conceptKey?: string): Promise<TutorExercise[]> {
  let query = supabase
    .from('tutor_exercises')
    .select('*')
    .order('created_at', { ascending: false });
  if (conceptKey) query = query.eq('concept_key', conceptKey);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return normalizeRows<TutorExercise>(data ?? []);
}

export async function createTutorExercise(
  exercise: Omit<TutorExercise, 'id' | 'userId' | 'createdAt'>,
): Promise<TutorExercise> {
  const userId = await getUserId();
  const { data, error } = await supabase
    .from('tutor_exercises')
    .insert({
      user_id: userId,
      session_id: exercise.sessionId ?? null,
      concept_key: exercise.conceptKey,
      exercise_type: exercise.exerciseType,
      prompt: exercise.prompt,
      questions: exercise.questions ?? null,
      user_answers: exercise.userAnswers ?? null,
      ai_feedback: exercise.aiFeedback ?? null,
      score: exercise.score ?? null,
      completed_at: exercise.completedAt ?? null,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return normalizeRow<TutorExercise>(data);
}

export async function updateTutorExercise(
  id: string,
  updates: Partial<Omit<TutorExercise, 'id' | 'userId' | 'createdAt'>>,
): Promise<void> {
  const map: Record<string, string> = {
    sessionId: 'session_id',
    conceptKey: 'concept_key',
    exerciseType: 'exercise_type',
    prompt: 'prompt',
    questions: 'questions',
    userAnswers: 'user_answers',
    aiFeedback: 'ai_feedback',
    score: 'score',
    completedAt: 'completed_at',
  };
  const snake: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(updates)) if (v !== undefined) snake[map[k] ?? k] = v;
  const { error } = await supabase.from('tutor_exercises').update(snake).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function callTutor(request: TutorRequest): Promise<TutorResponse> {
  const { data, error } = await supabase.functions.invoke('ai-tutor', { body: request });
  if (error) throw new Error(error.message);
  return data as TutorResponse;
}
